import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type { HoverIdleSnapshot } from "../types";
import { HoverTargetGridView } from "@/features/hoverIdle/hover/hoverTargetGridView";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import { ResourceHudView } from "@/features/hoverIdle/resources/resourceHudView";
import { CellUpgradePanelView } from "@/features/hoverIdle/upgrades/cellUpgradePanelView";
import { HovTimBalanceView } from "./hovTimBalanceView";
import { TimSkillView } from "./timSkillView";
import type { TimSkillSnapshot } from "./timSkillModel";
import type { TimSkillId } from "./timSkillDefinitions";
import type {
  HovTimBalancePair,
  HovTimBalanceTarget,
} from "@/features/hoverIdle/core/hoverIdleModel";
import {
  UpgradePanelView,
  type UpgradePanelState,
} from "@/features/hoverIdle/upgrades/upgradePanelView";
import {
  compareHuge,
  hugeZero,
  maxHuge,
  type HugeNumberInput,
} from "@/shared/numbers";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";

interface TimViewOptions {
  readonly container: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly onCellHoverChange: (
    cellIndex: number,
    isHovering: boolean,
  ) => boolean | void;
  readonly onUpgrade: () => boolean;
  readonly onUpgradeCell: (cellIndex: number) => boolean;
  readonly getUpgradeRepeatInitialIntervalMs?: () => number;
  readonly getUpgradeRepeatSpeedMultiplier?: () => number;
  readonly onTimSkillAcquire: (id: TimSkillId) => boolean;
  readonly getTimSkillSnapshot: () => TimSkillSnapshot;
  readonly onHovTimBalanceAssign: (target: HovTimBalanceTarget) => void;
  readonly onHovTimBalanceUnassign: (target: HovTimBalanceTarget) => void;
  readonly onHovTimBalanceRatioChange: (
    pair: HovTimBalancePair,
    ratio: number,
  ) => void;
  readonly onActiveTabChange?: (tabId: TimTargetTabId) => void;
  readonly onHovOLabelHoverChange?: (isHovering: boolean) => void;
  readonly getHovOLabelAchievementUnlocked?: () => boolean;
  readonly onTimHovActivationToggle?: () => void;
  readonly getTimHovActivationUnlocked?: () => boolean;
  // Hover-toggle duration setting (1 s / 2 s) for TimHov activation toggle.
  readonly getHoverToggleDurationMs?: () => number;
}

type TimTargetTabId = "main-hover" | "hov-tim-balance" | "tim-skill";

const TIM_SKILL_UNLOCK_HOV_THRESHOLD = 10000;
const UPGRADE_TOOLTIP_ID = "hover-idle-tim-upgrade-tooltip";

export class TimView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly mainTabButton: HTMLButtonElement;
  private readonly balanceTabButton: HTMLButtonElement;
  private readonly timSkillTabButton: HTMLButtonElement;
  private readonly mainPanel: HTMLDivElement;
  private readonly resourceHud: ResourceHudView;
  private readonly targetGrid: HoverTargetGridView;
  private readonly upgradePanel: UpgradePanelView;
  private readonly cellUpgradePanel: CellUpgradePanelView;
  private readonly balanceView: HovTimBalanceView;
  private readonly timSkillView: TimSkillView;
  private readonly getTimSkillSnapshot: () => TimSkillSnapshot;
  private readonly onActiveTabChange: ((tabId: TimTargetTabId) => void) | null;
  private activeTabId: TimTargetTabId = "main-hover";
  private isHovTimBalanceUnlocked = false;
  private isTimSkillUnlocked = false;
  private readonly getHovOLabelAchievementUnlocked: () => boolean;
  private readonly getTimHovActivationUnlocked: () => boolean;

  private readonly handleMainTabIntent = (): void => {
    this.setActiveTab("main-hover");
  };

  private readonly handleBalanceTabIntent = (): void => {
    if (!this.isHovTimBalanceUnlocked) return;
    this.setActiveTab("hov-tim-balance");
  };

  private readonly handleTimSkillTabIntent = (): void => {
    if (!this.isTimSkillUnlocked) return;
    this.setActiveTab("tim-skill");
  };

  constructor(options: TimViewOptions) {
    const messages = getMessages();
    this.root = document.createElement("div");
    this.root.className = "hover-idle-panel hover-idle-panel-tim-target";

    const tabs = document.createElement("div");
    tabs.className = "hover-idle-core-tabs";
    tabs.setAttribute("aria-label", messages.timView.tabsAria);

    this.mainTabButton = createCoreTabButton(messages.timView.mainTab);
    this.mainTabButton.dataset.coreTab = "main-hover";
    this.balanceTabButton = createCoreTabButton(messages.balance.title);
    this.balanceTabButton.dataset.coreTab = "hov-tim-balance";
    this.setTabAvailability(this.balanceTabButton, false);
    this.timSkillTabButton = createCoreTabButton(
      messages.timView.skillTabLocked(String(TIM_SKILL_UNLOCK_HOV_THRESHOLD)),
    );
    this.timSkillTabButton.dataset.coreTab = "tim-skill";
    this.timSkillTabButton.dataset.locked = "true";
    this.timSkillTabButton.disabled = true;
    this.timSkillTabButton.setAttribute("aria-disabled", "true");
    this.addTabIntentListeners(this.mainTabButton, this.handleMainTabIntent);
    this.addTabIntentListeners(
      this.balanceTabButton,
      this.handleBalanceTabIntent,
    );
    this.addTabIntentListeners(
      this.timSkillTabButton,
      this.handleTimSkillTabIntent,
    );
    tabs.append(
      this.mainTabButton,
      this.balanceTabButton,
      this.timSkillTabButton,
    );

    this.mainPanel = document.createElement("div");
    this.mainPanel.className = "hover-idle-target-panel";

    this.resourceHud = new ResourceHudView({
      pointerModel: options.pointerModel,
      onHovOLabelHoverChange: (isHovering) => {
        options.onHovOLabelHoverChange?.(isHovering);
      },
      onTimHovActivationToggle: () => {
        options.onTimHovActivationToggle?.();
      },
      getHoverToggleDurationMs: options.getHoverToggleDurationMs,
    });
    this.getHovOLabelAchievementUnlocked =
      options.getHovOLabelAchievementUnlocked ?? (() => false);
    this.getTimHovActivationUnlocked =
      options.getTimHovActivationUnlocked ?? (() => false);
    this.targetGrid = new HoverTargetGridView({
      variant: "tim",
      pointerModel: options.pointerModel,
      getGridTier: (snapshot) => snapshot.timGridTier,
      getHoveringCellIndex: (snapshot) => snapshot.timHoveringCellIndex,
      getTargetCells: (snapshot) => snapshot.timTargetCells,
      onCellHoverChange: options.onCellHoverChange,
    });
    this.upgradePanel = new UpgradePanelView({
      cardAriaLabel: messages.timView.upgradeCardAria,
      tooltip: {
        id: UPGRADE_TOOLTIP_ID,
        title: messages.timView.upgradeTooltip.title,
        region: messages.timView.region,
        summary: messages.timView.upgradeTooltip.summary,
        afterLabel: messages.timView.upgradeTooltip.afterLabel,
      },
      pointerModel: options.pointerModel,
      initialIntervalMs: options.getUpgradeRepeatInitialIntervalMs,
      speedMultiplier: options.getUpgradeRepeatSpeedMultiplier,
      onUpgrade: options.onUpgrade,
      render: renderTimGainUpgradePanel,
    });
    this.cellUpgradePanel = new CellUpgradePanelView({
      variant: "tim",
      labelPrefix: "T",
      costUnit: "Tim",
      gainIncrement: 0.05,
      unlockInitialGain: 0.05,
      gainSuffix: "/s",
      tooltipRegion: messages.timView.region,
      tooltipSummary: messages.timView.upgradeTooltip.summary,
      tooltipAfterLabel: messages.timView.upgradeTooltip.afterLabel,
      getGridTier: (snapshot) => snapshot.timGridTier,
      getTargetCells: (snapshot) => snapshot.timTargetCells,
      pointerModel: options.pointerModel,
      initialIntervalMs: options.getUpgradeRepeatInitialIntervalMs,
      speedMultiplier: options.getUpgradeRepeatSpeedMultiplier,
      onUpgradeCell: options.onUpgradeCell,
    });

    this.mainPanel.append(
      this.cellUpgradePanel.root,
      this.targetGrid.root,
      this.upgradePanel.root,
    );

    this.root.append(this.resourceHud.root, tabs, this.mainPanel);
    this.balanceView = new HovTimBalanceView({
      container: this.root,
      onAssign: options.onHovTimBalanceAssign,
      onUnassign: options.onHovTimBalanceUnassign,
      onRatioChange: options.onHovTimBalanceRatioChange,
      onNotificationChange: (hasUnassigned) => {
        this.balanceTabButton.dataset.unseen = hasUnassigned ? "true" : "false";
      },
      speedMultiplier: options.getUpgradeRepeatSpeedMultiplier,
    });
    this.timSkillView = new TimSkillView({
      container: this.root,
      onAcquireRequest: options.onTimSkillAcquire,
    });
    this.getTimSkillSnapshot = options.getTimSkillSnapshot;
    this.onActiveTabChange = options.onActiveTabChange ?? null;
    options.container.append(this.root);
    this.syncActiveTab();
  }

  update(
    snapshot: HoverIdleSnapshot,
    recentHovRatePerSecond: HugeNumberInput = 0,
  ): void {
    this.resourceHud.update(
      snapshot,
      recentHovRatePerSecond,
      this.getHovOLabelAchievementUnlocked(),
      this.getTimHovActivationUnlocked(),
    );
    this.targetGrid.update(snapshot);
    this.upgradePanel.update(snapshot);
    this.cellUpgradePanel.update(snapshot);
    this.timSkillView.update({
      tim: snapshot.tim,
      snapshot: this.getTimSkillSnapshot(),
    });
    this.syncHovTimBalanceUnlock(snapshot.unlocks.tabs.hovTimBalance);
    this.balanceView.update(snapshot);
    this.syncTimSkillUnlock(
      compareHuge(snapshot.hov, TIM_SKILL_UNLOCK_HOV_THRESHOLD) >= 0,
    );
  }

  restoreVirtualPointerFromHoverAnchor(): boolean {
    if (this.activeTabId !== "main-hover") {
      return false;
    }
    return this.targetGrid.restoreVirtualPointerFromHoverAnchor();
  }

  dispose(): void {
    this.removeTabIntentListeners(this.mainTabButton, this.handleMainTabIntent);
    this.removeTabIntentListeners(
      this.balanceTabButton,
      this.handleBalanceTabIntent,
    );
    this.removeTabIntentListeners(
      this.timSkillTabButton,
      this.handleTimSkillTabIntent,
    );
    this.resourceHud.dispose();
    this.targetGrid.dispose();
    this.upgradePanel.dispose();
    this.cellUpgradePanel.dispose();
    this.balanceView.dispose();
    this.timSkillView.dispose();
    this.root.remove();
  }

  private syncHovTimBalanceUnlock(unlocked: boolean): void {
    if (unlocked === this.isHovTimBalanceUnlocked) return;
    this.isHovTimBalanceUnlocked = unlocked;
    this.setTabAvailability(this.balanceTabButton, unlocked);
    if (!unlocked && this.activeTabId === "hov-tim-balance") {
      this.setActiveTab("main-hover");
    } else {
      this.syncActiveTab();
    }
  }

  private syncTimSkillUnlock(unlocked: boolean): void {
    const messages = getMessages();
    if (unlocked === this.isTimSkillUnlocked) return;
    this.isTimSkillUnlocked = unlocked;
    this.timSkillTabButton.textContent = unlocked
      ? messages.timView.skillTab
      : messages.timView.skillTabLocked(String(TIM_SKILL_UNLOCK_HOV_THRESHOLD));
    this.timSkillTabButton.dataset.locked = unlocked ? "false" : "true";
    this.timSkillTabButton.disabled = !unlocked;
    this.timSkillTabButton.setAttribute(
      "aria-disabled",
      unlocked ? "false" : "true",
    );
    if (!unlocked && this.activeTabId === "tim-skill") {
      this.setActiveTab("main-hover");
    }
  }

  private addTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.addEventListener("pointerenter", handler);
    button.addEventListener("focus", handler);
    button.addEventListener("click", handler);
  }

  private removeTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.removeEventListener("pointerenter", handler);
    button.removeEventListener("focus", handler);
    button.removeEventListener("click", handler);
  }

  private setActiveTab(tabId: TimTargetTabId): void {
    if (this.activeTabId === tabId) return;
    this.activeTabId = tabId;
    this.syncActiveTab();
    this.onActiveTabChange?.(tabId);
  }

  private syncActiveTab(): void {
    const isMainActive = this.activeTabId === "main-hover";
    const isBalanceActive = this.activeTabId === "hov-tim-balance";
    const isSkillActive = this.activeTabId === "tim-skill";
    this.mainPanel.hidden = !isMainActive;
    this.balanceView.setVisible(isBalanceActive);
    this.timSkillView.setVisible(isSkillActive);
    this.mainTabButton.dataset.active = isMainActive ? "true" : "false";
    this.balanceTabButton.dataset.active = isBalanceActive ? "true" : "false";
    this.timSkillTabButton.dataset.active = isSkillActive ? "true" : "false";
    this.mainTabButton.setAttribute(
      "aria-selected",
      isMainActive ? "true" : "false",
    );
    this.balanceTabButton.setAttribute(
      "aria-selected",
      isBalanceActive ? "true" : "false",
    );
    this.timSkillTabButton.setAttribute(
      "aria-selected",
      isSkillActive ? "true" : "false",
    );
  }

  private setTabAvailability(
    button: HTMLButtonElement,
    unlocked: boolean,
  ): void {
    button.hidden = !unlocked;
    button.dataset.locked = unlocked ? "false" : "true";
    button.disabled = !unlocked;
    button.setAttribute("aria-disabled", unlocked ? "false" : "true");
  }
}

function createCoreTabButton(label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "hover-idle-core-tab";
  button.textContent = label;
  return button;
}

function renderTimGainUpgradePanel(
  snapshot: HoverIdleSnapshot,
): UpgradePanelState {
  const messages = getMessages();
  const effectText = messages.timView.upgradeEffect(
    formatForContext(snapshot.timUpgradeGainIncrease, "compact"),
  );
  const nextTimGain = snapshot.timGainPerSecond.add(
    snapshot.timUpgradeGainIncrease,
  );
  const missingTim = maxHuge(
    hugeZero(),
    snapshot.timUpgradeCost.sub(snapshot.tim),
  );

  return {
    effectText,
    costText: messages.timView.upgradeCost(
      formatForContext(snapshot.timUpgradeCost, "compact"),
    ),
    cardLabelText: effectText,
    isAffordable: snapshot.canPurchaseTimUpgrade,
    tooltipEffectText: messages.timView.upgradeEffect(
      formatForContext(snapshot.timUpgradeGainIncrease, "tooltip"),
    ),
    tooltipAfterText: messages.timView.upgradeAfter(
      formatForContext(snapshot.timGainPerSecond, "tooltip"),
      formatForContext(nextTimGain, "tooltip"),
    ),
    tooltipCostText: `${formatForContext(snapshot.timUpgradeCost, "tooltip")} Tim`,
    tooltipStateText: snapshot.canPurchaseTimUpgrade
      ? messages.timView.upgradeStateReady
      : messages.timView.upgradeStateNeed(
          formatForContext(missingTim, "tooltip"),
        ),
  };
}
